import { Campus } from '../types';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  MapPin,
  Phone,
  Clock,
  Users,
  Unlink2,
  Link2,
  MoreVertical,
  Edit,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface CampusCardProps {
  campus: Campus;
  isAttached?: boolean;
  onAttach?: () => void;
  onDetach?: () => void;
  onManageUsers?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onSyncPlaces?: () => void;
  isLoading?: boolean;
  isSyncing?: boolean;
}

export function CampusCard({
  campus,
  isAttached = false,
  onAttach,
  onDetach,
  onManageUsers,
  onEdit,
  onDelete,
  onSyncPlaces,
  isLoading = false,
  isSyncing = false,
}: CampusCardProps) {
  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h3 className="font-semibold text-lg leading-none">
              {campus.campus_name}
            </h3>
            {campus.campus_short_name && (
              <p className="text-sm text-muted-foreground">
                {campus.campus_short_name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={campus.is_active ? 'default' : 'secondary'}>
              {campus.is_active ? 'Active' : 'Inactive'}
            </Badge>
            {(onEdit || onDelete || onSyncPlaces) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onSyncPlaces && (
                    <DropdownMenuItem onClick={onSyncPlaces} disabled={isSyncing}>
                      <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                      {isSyncing ? 'Syncing...' : 'Sync Places'}
                    </DropdownMenuItem>
                  )}
                  {onSyncPlaces && (onEdit || onDelete) && <DropdownMenuSeparator />}
                  {onEdit && (
                    <DropdownMenuItem onClick={onEdit}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Campus
                    </DropdownMenuItem>
                  )}
                  {onEdit && onDelete && <DropdownMenuSeparator />}
                  {onDelete && (
                    <DropdownMenuItem
                      onClick={onDelete}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Campus
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Location */}
        <div className="flex items-start gap-2 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div className="flex-1">
            <p className="leading-tight">{campus.address}</p>
            <p className="text-muted-foreground">
              {campus.city}, {campus.state} {campus.zip}
            </p>
          </div>
        </div>

        {/* Phone */}
        {campus.phone_number && (
          <div className="flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span>{campus.phone_number}</span>
          </div>
        )}

        {/* Time Zone */}
        {campus.time_zone && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>{campus.time_zone}</span>
          </div>
        )}

      </CardContent>

      <CardFooter className="pt-3 pb-3 flex gap-2">
        {onManageUsers && (
          <Button
            variant="outline"
            size="sm"
            onClick={onManageUsers}
            disabled={isLoading}
            className="flex-1"
          >
            <Users className="h-4 w-4 mr-1" />
            Users
          </Button>
        )}
        {isAttached && onDetach && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDetach}
            disabled={isLoading}
            className="flex-1"
          >
            <Unlink2 className="h-4 w-4 mr-1" />
            Detach
          </Button>
        )}
        {!isAttached && onAttach && (
          <Button
            variant="default"
            size="sm"
            onClick={onAttach}
            disabled={isLoading}
            className="flex-1"
          >
            <Link2 className="h-4 w-4 mr-1" />
            Attach to School
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}